import { useState, useCallback, useRef, useEffect } from "react";
import type { Message } from "@shared/types";
import { trpc } from "./use-trpc";
import { supabase } from "../lib/supabase";

const CHAT_STORAGE_KEY = "mentor-chat-messages";
const MAX_PERSISTED_MESSAGES = 50;

const STREAM_TIMEOUT_MS: Record<string, number> = {
  "claude-code": 60_000,
  "cowork": 120_000,
} as const;

function getStreamTimeout(): number {
  const mode = localStorage.getItem("mentor-chat-mode") ?? "claude-code";
  return STREAM_TIMEOUT_MS[mode] ?? 60_000;
}

function friendlyError(status: number, message: string): string {
  if (status === 401) return "Sessao expirada. Faca login novamente.";
  if (status === 429) return "Limite de requisicoes atingido. Aguarde um momento.";
  if (status === 500 || status === 503) return "Servidor temporariamente indisponivel. Tente novamente.";
  if (message.includes("timeout") || message.includes("aborted")) return "A resposta demorou demais. Tente uma pergunta mais curta.";
  return "Ocorreu um erro. Tente novamente.";
}

function loadPersistedMessages(): Message[] {
  try {
    const raw = localStorage.getItem(CHAT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Message[];
    return Array.isArray(parsed) ? parsed.slice(-MAX_PERSISTED_MESSAGES) : [];
  } catch {
    return [];
  }
}

function persistMessages(messages: Message[]): void {
  try {
    const toStore = messages.slice(-MAX_PERSISTED_MESSAGES);
    localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify(toStore));
  } catch {
    // localStorage full or unavailable
  }
}

interface SourceInfo {
  skill_name: string;
  category: string;
  source_file: string;
  chunk_type: string;
}

interface UseStreamingChatReturn {
  messages: Message[];
  isStreaming: boolean;
  currentResponse: string;
  error: string | null;
  conversationId: string | null;
  sendMessage: (content: string) => Promise<void>;
  stopStreaming: () => void;
  clearChat: () => void;
  loadConversation: (id: string) => Promise<void>;
  submitFeedback: (messageId: string, feedback: "up" | "down" | null) => Promise<void>;
}

function parseSSE(text: string): Array<{ event: string; data: string }> {
  const events: Array<{ event: string; data: string }> = [];
  const lines = text.split("\n");
  let currentEvent = "";
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith(":")) continue; // SSE comment
    if (line.startsWith("event:")) {
      currentEvent = line.slice(6).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    } else if (line === "") {
      if (dataLines.length > 0) {
        events.push({
          event: currentEvent || "message",
          data: dataLines.join("\n"),
        });
      }
      currentEvent = "";
      dataLines.length = 0;
    }
  }
  return events;
}

export function useStreamingChat(_userId: string): UseStreamingChatReturn {
  const [messages, setMessages] = useState<Message[]>(loadPersistedMessages);
  const [isStreaming, setIsStreaming] = useState(false);
  const [currentResponse, setCurrentResponse] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);

  // Persist messages to localStorage on every change
  useEffect(() => {
    persistMessages(messages);
  }, [messages]);

  const abortRef = useRef<AbortController | null>(null);
  const sourcesRef = useRef<SourceInfo[]>([]);

  const utils = trpc.useUtils();

  const feedbackMutation = trpc.chat.feedback.useMutation();

  const sendMessage = useCallback(
    async (content: string) => {
      if (isStreaming) return;

      setError(null);
      setCurrentResponse("");
      sourcesRef.current = [];

      // Optimistically add user message
      const userMsg: Message = {
        id: crypto.randomUUID(),
        conversation_id: conversationId ?? "",
        role: "user",
        content,
        sources: null,
        feedback: null,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;
      let accumulated = "";
      let timedOut = false;

      try {
        const { data: sessionData } = await supabase.auth.getSession();
        const token = sessionData.session?.access_token;

        const response = await fetch("/api/chat/stream", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            query: content,
            conversation_id: conversationId ?? undefined,
            mode: localStorage.getItem("mentor-chat-mode") ?? "claude-code",
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(friendlyError(response.status, response.statusText));
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error("No response body");

        const timeoutMs = getStreamTimeout();
        const decoder = new TextDecoder();
        let buffer = "";
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const resetTimeout = () => {
          if (timeoutId) clearTimeout(timeoutId);
          timeoutId = setTimeout(() => {
            timedOut = true;
            reader.cancel().catch(() => {});
            controller.abort();
          }, timeoutMs);
        };

        resetTimeout();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          resetTimeout();

          buffer += decoder.decode(value, { stream: true });
          const events = parseSSE(buffer);

          // Keep unprocessed remainder in buffer
          const lastNewline = buffer.lastIndexOf("\n\n");
          if (lastNewline >= 0) {
            buffer = buffer.slice(lastNewline + 2);
          }

          for (const event of events) {
            switch (event.event) {
              case "token": {
                const parsed = JSON.parse(event.data) as { token: string };
                accumulated += parsed.token;
                setCurrentResponse(accumulated);
                break;
              }
              case "sources": {
                const parsed = JSON.parse(event.data) as { sources: SourceInfo[] };
                sourcesRef.current = parsed.sources;
                break;
              }
              case "done": {
                const parsed = JSON.parse(event.data) as {
                  conversation_id: string;
                  message_id?: string;
                };
                setConversationId(parsed.conversation_id);

                // Finalize assistant message (prefer server-assigned ID for feedback)
                const assistantMsg: Message = {
                  id: parsed.message_id ?? crypto.randomUUID(),
                  conversation_id: parsed.conversation_id,
                  role: "assistant",
                  content: accumulated,
                  sources: sourcesRef.current as unknown as Record<string, unknown>[],
                  feedback: null,
                  created_at: new Date().toISOString(),
                };

                setMessages((prev) => [...prev, assistantMsg]);
                setCurrentResponse("");
                setIsStreaming(false);
                break;
              }
              case "error": {
                const parsed = JSON.parse(event.data) as { message: string };
                setError(parsed.message);
                setIsStreaming(false);
                break;
              }
            }
          }
        }

        if (timeoutId) clearTimeout(timeoutId);
      } catch (err) {
        if (timedOut) {
          // Timeout — save partial response if available
          if (accumulated.length > 0) {
            const partialMsg: Message = {
              id: crypto.randomUUID(),
              conversation_id: conversationId ?? "",
              role: "assistant",
              content: accumulated + "\n\n_[Resposta parcial — timeout]_",
              sources: sourcesRef.current.length > 0
                ? (sourcesRef.current as unknown as Record<string, unknown>[])
                : null,
              feedback: null,
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, partialMsg]);
            setCurrentResponse("");
          }
          setError(friendlyError(0, "timeout"));
        } else if (err instanceof DOMException && err.name === "AbortError") {
          // User aborted — finalize partial response
          if (accumulated.length > 0) {
            const partialMsg: Message = {
              id: crypto.randomUUID(),
              conversation_id: conversationId ?? "",
              role: "assistant",
              content: accumulated,
              sources: null,
              feedback: null,
              created_at: new Date().toISOString(),
            };
            setMessages((prev) => [...prev, partialMsg]);
            setCurrentResponse("");
          }
        } else {
          const message = err instanceof Error ? err.message : "Erro de conexao";
          setError(friendlyError(0, message));
        }
        setIsStreaming(false);
      } finally {
        abortRef.current = null;
      }
    },
    [isStreaming, conversationId],
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  const clearChat = useCallback(() => {
    setMessages([]);
    setCurrentResponse("");
    setError(null);
    setConversationId(null);
    sourcesRef.current = [];
    try {
      localStorage.removeItem(CHAT_STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const loadConversation = useCallback(
    async (id: string) => {
      setConversationId(id);
      setError(null);
      setCurrentResponse("");

      const data = await utils.chat.messages.fetch({ conversation_id: id });
      setMessages(
        (data ?? []).map((m) => ({
          id: m.id as string,
          conversation_id: m.conversation_id as string,
          role: m.role as "user" | "assistant" | "system",
          content: m.content as string,
          sources: m.sources as Record<string, unknown>[] | null,
          feedback: m.feedback as "up" | "down" | null,
          created_at: m.created_at as string,
        })),
      );
    },
    [utils.chat.messages],
  );

  const submitFeedback = useCallback(
    async (messageId: string, feedback: "up" | "down" | null) => {
      await feedbackMutation.mutateAsync({ message_id: messageId, feedback });
      setMessages((prev) =>
        prev.map((m) => (m.id === messageId ? { ...m, feedback } : m)),
      );
    },
    [feedbackMutation],
  );

  return {
    messages,
    isStreaming,
    currentResponse,
    error,
    conversationId,
    sendMessage,
    stopStreaming,
    clearChat,
    loadConversation,
    submitFeedback,
  };
}
